import { FunctionComponent, SyntheticEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useSubscription, gql } from '@apollo/client';

import {
  FRAGMENT_BATTLE_PASS_USER_QUEST_FILEDS,
  FRAGMENT_EXTERNAL_ACCOUNT_FILEDS,
  useConfig,
  useGetStatistics,
} from '~components/hooks';
import {
  Live,
  TopMenu,
  Languages,
  SiteSound,
  Search,
} from '~components/header';
import Currency from '~components/Currency';
import { Price } from '~ui/index';
import {
  FRAGMENT_ACTIVE_MODAL_FILEDS,
  GET_ACTIVE_MODAL,
} from '~components/hooks';
import { useProfile } from '~components/profile/hooks';
import { useYaMetrica } from '~frontend/provider/yametrica';

import ErrorBoundary from '../error/ErrorBoundary';

import Logo from '../../scss/images/logo.svg';
import UserIcon from '../../scss/images/access_time_filled.svg?react';

import { GET_HISTORY } from '~components/profile/items';
import { SELL_ALL_ITEMS } from '~components/profile/SellAllItems';
import { ActiveGames, BattlePassUserQuest } from 'graphql';
import IconBalanceBattlePass from '../../scss/images/icon-balance-battlepass.svg';

const Online = () => {
  const { data: { getStatistics } = {} } = useGetStatistics();

  return <>{getStatistics?.online}</>;
};

interface IProfileBot {
  children: React.ReactNode;
}

const ProfileBot: FunctionComponent<IProfileBot> = ({ children }) => {
  const yametrica = useYaMetrica();

  const reachGoal = (event: SyntheticEvent<HTMLElement>) => {
    yametrica.reachGoal(event.currentTarget.getAttribute('data-reachgoal'));
  };

  return (
    <Link
      to="/pay"
      className={`profile-money-sec profile-money-sec-ab`}
      onClick={reachGoal}
      data-reachgoal="add_funds_header"
    >
      {children}
      <div className="add-money-v2"></div>
    </Link>
  );
};

const Header: FunctionComponent = () => {
  const yametrica = useYaMetrica();
  const { t } = useTranslation();
  const getConfig = useConfig();
  const getProfile = useProfile();

  useSubscription(
    gql`
      subscription updateActiveModal {
        updateActiveModal {
          ...ActiveModalFields
        }
      }

      ${FRAGMENT_ACTIVE_MODAL_FILEDS}
    `,
    {
      onData: ({ client, data }) => {
        client.cache.writeQuery({
          query: GET_ACTIVE_MODAL,
          data: {
            getActiveModal: data?.data?.updateActiveModal,
          },
        });
      },
    },
  );

  useSubscription(
    gql`
      subscription updateBattlePassLevel {
        updateBattlePassLevel {
          id
          isOpen
        }
      }
    `,
  );

  useSubscription(
    gql`
      subscription updateProfile {
        updateProfile {
          id
          userName
          avatar
          money
          battlePassCoins
          coins
          referralMoney
          getWheelStep
          lastWheelGame
          joinedToGroup
          getSteam @client
          getVk @client
          getLastFreeGame {
            id
          }
        }
      }
    `,
  );

  useSubscription(
    gql`
      subscription updateProfileExternalAccount {
        updateProfileExternalAccount {
          id
          getExternalAccounts {
            ...ExternalAccountFields
          }
          getSteam @client
          getVk @client
        }
      }

      ${FRAGMENT_EXTERNAL_ACCOUNT_FILEDS}
    `,
  );

  useSubscription(
    gql`
      subscription updateExternalAccount {
        updateExternalAccount {
          ...ExternalAccountFields
        }
      }

      ${FRAGMENT_EXTERNAL_ACCOUNT_FILEDS}
    `,
  );

  useSubscription<{ createBattlePassUserQuest: BattlePassUserQuest }>(
    gql`
      subscription createBattlePassUserQuest {
        createBattlePassUserQuest {
          ...BattlePassUserQuestFields
        }
      }

      ${FRAGMENT_BATTLE_PASS_USER_QUEST_FILEDS}
    `,
    {
      onData: ({ client, data: { data } }) => {
        if (!data?.createBattlePassUserQuest) {
          return false;
        }

        client.cache.writeFragment({
          id: `BattlePassQuest:${data.createBattlePassUserQuest.battlePassQuestId}`,
          fragment: gql`
            fragment UpdateBattlePassQuest on BattlePassQuest {
              getBattlePassUserQuest {
                ...BattlePassUserQuestFields
              }
            }
            ${FRAGMENT_BATTLE_PASS_USER_QUEST_FILEDS}
          `,
          fragmentName: 'UpdateBattlePassQuest',
          data: {
            getBattlePassUserQuest: data?.createBattlePassUserQuest,
          },
        });
      },
    },
  );

  useSubscription(
    gql`
      subscription updateBattlePassUserQuest {
        updateBattlePassUserQuest {
          ...BattlePassUserQuestFields
        }
      }

      ${FRAGMENT_BATTLE_PASS_USER_QUEST_FILEDS}
    `,
  );

  useSubscription(
    gql`
      subscription addGame {
        addGame {
          ...GameFields
        }
      }

      ${GET_HISTORY.fragments.games}
    `,
    {
      onData: ({
        client,
        data: {
          data: { addGame },
        },
      }) => {
        if (!addGame) {
          return null;
        }

        const data = client.readQuery<{
          getHistory: ActiveGames;
        }>({
          query: GET_HISTORY.query,
          variables: {
            input: {
              userId: getProfile.id,
              isAwait: false,
            },
          },
        });

        const dataSellitems = client.readQuery({
          query: SELL_ALL_ITEMS,
          variables: {
            userId: getProfile.id,
          },
        });

        const getHistory = data?.getHistory;

        if (getHistory && _.find(getHistory?.getGames, ['id', addGame?.id])) {
          return;
        }

        const getStatsSellitems = dataSellitems?.getStats;

        if (getStatsSellitems && addGame.caseId !== 11) {
          const price =
            addGame?.status === 1 ? addGame?.priceSell : addGame?.price;
          client.cache.writeQuery({
            query: SELL_ALL_ITEMS,
            data: {
              getStats: {
                sumItems: getStatsSellitems.sumItems + price,
                sumItemsOld: getStatsSellitems.sumItemsOld + addGame.price,
              },
            },
            variables: { userId: getProfile.id },
          });
        }

        if (getHistory) {
          client.writeQuery({
            query: GET_HISTORY.query,
            data: {
              getHistory: {
                count: getHistory.count + 1,
                getGames: _.orderBy(
                  [addGame, ..._.toArray(getHistory?.getGames)],
                  ['id'],
                  ['desc'],
                ),
              },
            },
            variables: {
              input: {
                userId: getProfile.id,
                isAwait: false,
              },
            },
          });
        }
      },
    },
  );

  const reachGoal = (
    event: SyntheticEvent<HTMLElement>,
    { reachgoal } = event?.currentTarget?.dataset ||
      ((event.target as HTMLElement)?.dataset as undefined),
  ) => {
    yametrica.reachGoal(reachgoal);
  };

  return (
    <header className="header">
      <div className="top-drop-name">
        <span>Top drop</span>
      </div>
      <div className="top-drop-title">
        <div className="top-drop-title-wrap">
          <span>live</span>
          <span />
          <span>
            <UserIcon />
            <Online /> {t('Online')}
          </span>
        </div>
      </div>
      <ErrorBoundary>
        <Live />
      </ErrorBoundary>
      <div className="header-wrap">
        <div className="header-left">
          <Link
            className="logo main_logo"
            to="/"
            data-audio="3.mp3"
            data-volume="0.15"
          >
            <img src={Logo} alt="logo" width={210} height={180} />
          </Link>
        </div>
        <div className="header-center">
          <ErrorBoundary>
            <TopMenu />
          </ErrorBoundary>
          <ErrorBoundary>
            <Search />
          </ErrorBoundary>
        </div>
        <div className="header-right">
          <Languages />
          <SiteSound />
          {getProfile ? (
            <div className="profile">
              <div className="profile-top">
                <Link className="name go_profile" to="/profile">
                  <img src={getProfile.avatar} alt={getProfile.avatar} />
                  <span>{getProfile.userName}</span>
                </Link>
                <a className="logout" href="/auth/logout">
                  <div className="icon material-icons">logout</div>
                </a>
              </div>
              <div className="profile-bot">
                <ProfileBot>
                  <div className="profile-money-sec-money-default">
                    <span>
                      <Price sum={getProfile?.money} />
                    </span>
                    <Currency />
                  </div>
                  <div className="profile-money-sec-money-battlepass">
                    <span>
                      <Price sum={getProfile?.battlePassCoins} />
                    </span>
                    <img src={IconBalanceBattlePass} />
                  </div>
                </ProfileBot>
              </div>
            </div>
          ) : (
            <div className="authorization">
              <div className="open-cases-btns">
                <a
                  className="advantages-sign-in-steam"
                  href="/auth/steam"
                  onClick={reachGoal}
                  data-reachgoal="steam_auth"
                >
                  <div className="small-text">{t('Sign in through')}</div>
                  <div className="text">Steam</div>
                  <div className="top"></div>
                  <div className="bot-2"></div>
                </a>
              </div>
              <div className="authorization-social">
                {getConfig?.hostname !== 'cscase.com' && (
                  <a
                    className="vk"
                    href="/auth/vk"
                    onClick={reachGoal}
                    data-reachgoal="vk_auth"
                  ></a>
                )}

                {getConfig?.hostname === 'cscase.com' && (
                  <a
                    className="google"
                    href="/auth/google"
                    onClick={reachGoal}
                    data-reachgoal="g_auth"
                  >
                    <div className="top"></div>
                  </a>
                )}

                <a
                  className="fb"
                  href="/auth/fb"
                  onClick={reachGoal}
                  data-reachgoal="fb_auth"
                ></a>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
